/**
 * Declarative `<title>` and `<meta name="description">` for app routes.
 *
 * Editors add or change copy here; both the client (`RouteMeta` in
 * `routeMeta.tsx`) and the Express host (`host/src/meta.ts`) read from this
 * single registry, so browser tabs, initial HTML, and social link previews
 * stay in sync.
 *
 * This file must stay DOM-free and React-free so the host can import it.
 */
import {
  FORGE_INFERENCE_PATH,
  FORGE_INFERENCE_PATHS,
} from '../../inference/paths';
import {ACCOUNT_SETTINGS_PATHS} from '../../routes/account/accountSettingPaths';
import {ADMIN_GLUE_PATH} from '../../routes/admin/adminPaths';
import {AUTOMATIONS_PATH} from '../../routes/automations/automationsPaths';
import {
  INFERENCE_COLLECTION_PATH,
  INFERENCE_COMPARE_PATH,
  INFERENCE_MODEL_PATH,
} from '../../routes/inference/inferencePaths';
import {
  ENTITY_LAUNCH_PATHS,
  PROJECT_LAUNCH_TAB_PATH,
} from '../../routes/launch/launchPaths';
import {
  FORGE_NOTEBOOK_PATH,
  NOTEBOOKS_HOME_PATHS,
} from '../../routes/notebooks/notebookPaths';
import {QUICKSTART_PATH} from '../../routes/onboarding/onboardingPaths';
import {ORG_NESTED_ROUTES} from '../../routes/organization/orgNestedRoutes';
import {HOME_PATH} from '../../routes/paths';
import {REGISTRY_NESTED_ROUTES} from '../../routes/registry/registryNestedRoutes';
import {GLOBAL_SANDBOXES_PATHS} from '../../routes/sandboxes/sandboxesPaths';
import {getModelLabelById} from './modelLabelLookup';

export type RouteMeta = {
  /** Title template; `{varName}` placeholders are resolved via `resolveVars` or `defaultVars`. */
  title: string;
  /** Description template; `{varName}` placeholders are resolved via `resolveVars` or `defaultVars`. */
  description: string;
  /** Fallback values for placeholders when `resolveVars` omits them (e.g. unknown param). */
  defaultVars?: Record<string, string>;
  /**
   * Synchronous resolver from URL params to placeholder values. Must stay
   * DOM-free / React-free so the SSR host can call it too.
   */
  resolveVars?: (params: Record<string, string>) => Record<string, string>;
};

export type RouteMetaRegistryEntry = {
  path: string | string[];
  exact: boolean;
  meta: RouteMeta;
};

/**
 * Ordered most-specific first. `matchPath` in `resolveRouteMeta` returns the
 * first match, so nested templated paths (e.g. `/inference/:collectionId/:modelId`)
 * must appear before their broader siblings (e.g. `/inference/:collectionId?`).
 */
export const ROUTE_META_REGISTRY: RouteMetaRegistryEntry[] = [
  {
    path: [FORGE_INFERENCE_PATH, ...FORGE_INFERENCE_PATHS],
    exact: true,
    meta: {
      title: 'Inference',
      description:
        'Explore CoreWeave-hosted models, compare pricing and capabilities, and try prompts in the Inference playground.',
    },
  },
  {
    path: GLOBAL_SANDBOXES_PATHS,
    exact: true,
    meta: {
      title: 'Sandboxes',
      description: 'Create and manage sandboxes and snapshots on CoreWeave.',
    },
  },
  {
    path: [...NOTEBOOKS_HOME_PATHS, FORGE_NOTEBOOK_PATH],
    exact: true,
    meta: {
      title: 'Notebooks',
      description: 'Create and run interactive notebooks on CoreWeave.',
    },
  },
  {
    path: [INFERENCE_MODEL_PATH],
    exact: true,
    meta: {
      title: '{modelName} on Serverless Inference',
      description:
        'Pricing, context window, capabilities, and usage examples for {modelName} hosted on Serverless Inference.',
      defaultVars: {modelName: 'Model'},
      resolveVars: ({modelId}): Record<string, string> => {
        if (!modelId) {
          return {};
        }
        const label = getModelLabelById(modelId);
        return label != null ? {modelName: label} : {};
      },
    },
  },
  {
    path: [INFERENCE_COMPARE_PATH],
    exact: true,
    meta: {
      title: 'Compare models on Serverless Inference',
      description:
        'Side-by-side comparison of Serverless Inference hosted models across pricing, latency, context window, and capabilities.',
    },
  },
  {
    path: [INFERENCE_COLLECTION_PATH],
    exact: true,
    meta: {
      title: 'Serverless Inference hosted models',
      description:
        'Serverless Inference models: A catalog of CoreWeave-hosted open-source models with transparent per-token pricing.',
    },
  },
  {
    path: [QUICKSTART_PATH],
    exact: true,
    meta: {
      title: 'Quickstarts for W&B Models, Weave, and Serverless Inference',
      description:
        'Get started with Weights & Biases: Install the SDK, log your first run, and explore W&B Models, Weave, and Serverless Inference.',
    },
  },
  {
    path: ['/', HOME_PATH],
    exact: true,
    meta: {
      title: 'Home',
      description:
        'Your W&B home: recent projects, reports, and activity across your teams.',
    },
  },
  // Routes served by `HomePageWrapper` that have no page-level meta. Declared
  // here so `RouteMeta` (and the SSR host) own their titles, instead of a
  // wrapper-level fallback that could clobber pages which set their own title.
  {
    path: [...ENTITY_LAUNCH_PATHS, PROJECT_LAUNCH_TAB_PATH],
    exact: true,
    meta: {
      title: 'Launch',
      description:
        'Configure and run W&B Launch jobs, agents, and queues for your team.',
    },
  },
  {
    path: AUTOMATIONS_PATH,
    exact: true,
    meta: {
      title: 'Automations',
      description:
        'Create and manage W&B automations that trigger actions on your runs and artifacts.',
    },
  },
  {
    path: ACCOUNT_SETTINGS_PATHS,
    exact: true,
    meta: {
      title: 'Account settings',
      description:
        'Manage your W&B account, organization, and billing settings.',
    },
  },
  {
    path: REGISTRY_NESTED_ROUTES.allPaths,
    exact: true,
    meta: {
      title: 'Registry',
      description:
        'Browse and manage your W&B Registry of models, datasets, and other artifacts.',
    },
  },
  {
    path: ORG_NESTED_ROUTES.allPaths,
    exact: true,
    meta: {
      title: 'Organization',
      description:
        'Manage your W&B organization: teams, members, roles, and usage.',
    },
  },
  {
    path: ADMIN_GLUE_PATH,
    exact: true,
    meta: {
      title: 'Admin',
      description: 'W&B administration tools.',
    },
  },
];

const PLACEHOLDER_RE = /\{([a-zA-Z0-9_]+)\}/g;

/**
 * Resolves `{varName}` placeholders in `meta.title` / `meta.description`.
 * Merges `meta.defaultVars` (config fallbacks) with the supplied `vars`
 * (page-supplied or `resolveVars`-supplied), with supplied values winning.
 * Unresolved placeholders are left as the literal `{name}` token so bugs
 * surface visibly instead of silently disappearing.
 */
export function substituteMeta(
  meta: RouteMeta,
  vars?: Record<string, string>
): {title: string; description: string} {
  const merged: Record<string, string> = {
    ...meta.defaultVars,
    ...vars,
  };
  const sub = (template: string): string =>
    template.replace(PLACEHOLDER_RE, (full, name) =>
      merged[name] != null ? merged[name] : full
    );
  return {title: sub(meta.title), description: sub(meta.description)};
}

/** Client `DEFAULT_META` must match host `defaultMetadata` to avoid hydration flicker. */
export const DEFAULT_META: RouteMeta = {
  title: 'Weights & Biases',
  description: 'Weights & Biases, developer tools for machine learning',
};
